import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react'
import { Modal } from './Modal'

export interface ConfirmOptions {
  /** Header text. Defaults to „Potvrdenie". */
  title?: string
  /** Body — the question being asked. */
  message: ReactNode
  /** Confirm button label. Defaults to „Potvrdiť". */
  confirmText?: string
  /** Cancel button label. Defaults to „Zrušiť". */
  cancelText?: string
  /** Render the confirm button in the destructive (red) style. */
  danger?: boolean
}

type ConfirmFn = (opts: ConfirmOptions | string) => Promise<boolean>

const ConfirmContext = createContext<ConfirmFn | null>(null)

/**
 * Promise-based confirmation dialog — a styled replacement for `window.confirm`.
 * Wrap the app once in <ConfirmProvider>, then `const confirm = useConfirm()` and
 * `if (await confirm('Zmazať?')) …`. A bare string is shorthand for `{ message }`.
 */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [options, setOptions] = useState<ConfirmOptions | null>(null)
  const resolverRef = useRef<((value: boolean) => void) | null>(null)

  const confirm = useCallback<ConfirmFn>((opts) => {
    const normalized = typeof opts === 'string' ? { message: opts } : opts
    setOptions(normalized)
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve
    })
  }, [])

  const close = useCallback((result: boolean) => {
    resolverRef.current?.(result)
    resolverRef.current = null
    setOptions(null)
  }, [])

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {options && (
        <Modal title={options.title ?? 'Potvrdenie'} onClose={() => close(false)} size="sm">
          <div className="p-5 space-y-4">
            <div className="text-sm text-gray-600 dark:text-gray-300">{options.message}</div>
            <div className="flex justify-end gap-2">
              <button type="button" className="btn-secondary" onClick={() => close(false)}>
                {options.cancelText ?? 'Zrušiť'}
              </button>
              <button
                type="button"
                className={options.danger ? 'btn-danger' : 'btn-primary'}
                onClick={() => close(true)}
                autoFocus
              >
                {options.confirmText ?? 'Potvrdiť'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </ConfirmContext.Provider>
  )
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext)
  if (!ctx) throw new Error('useConfirm must be used within <ConfirmProvider>')
  return ctx
}
